import React from 'react'
import {
  Anchor,
  Div,
  Flex,
  IconLink,
  Input,
  PhoneInput,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { useEffect, useState } from 'src/hooks'
import { PhoneNumberInput } from 'src/graphql/generated/graphql'
import { getFormattedNumber } from 'src/onboarding/components/PhoneNumberVerification'
import usePhoneNumberCountries from 'src/hooks/usePhoneNumberCountries'
import {
  createPhoneNumberInput,
  getValidatedNumber,
} from 'src/helpers/createPhoneNumberInput'
import { getMachineCountryCode } from 'src/helpers'
import { PhoneVerificationTerms } from 'src/dashboard/components/PhoneVerificationTerms'
import WhyMyPhoneNumberDialog from 'src/phone_number_verification/components/WhyMyPhoneNumberDialog'
import { Checkbox, FormControlLabel } from '@material-ui/core'

const FONT_FAMILY = 'Montserrat, sans-serif'

const PhoneNumberEntry = (props: {
  requestVerificationCode: (
    phoneNumber: PhoneNumberInput,
    firstName?: string,
    lastName?: string,
    hasSMSOptIn?: boolean,
  ) => void
  anchorButton?: {
    label: string
    action: () => void
  }
  title: string
  subtitle?: string
  validateButtonTitle?: string
  phoneNumber?: PhoneNumberInput
  failureMessage?: string
  isCreateOrLogin: boolean
  shouldAskFirstAndLastName: boolean
  shouldDisplaySmsOptIn: boolean
}) => {
  const {
    requestVerificationCode,
    anchorButton,
    title,
    subtitle,
    validateButtonTitle,
    failureMessage,
    isCreateOrLogin,
    shouldAskFirstAndLastName,
    shouldDisplaySmsOptIn,
  } = props
  const [isDialogOpen, setDialogIsOpen] = useState(false)

  const phoneNumberCountries = usePhoneNumberCountries()

  const [selectedCountryIndex, setSelectedCountryIndex] = useState(
    phoneNumberCountries.findIndex(
      country =>
        country.isoCode === (props.phoneNumber?.countryIsoCode ?? 'US'),
    ),
  )
  const [firstName, setFirstName] = useState<string | undefined>()
  const [lastName, setLastName] = useState<string | undefined>()
  const [phoneNumber, setPhoneNumber] = useState<string>(
    props.phoneNumber?.nationalNumber ?? '',
  )
  const [hasSMSOptIn, setHasSMSOptIn] = useState(true)
  const [isCountrySelectedManually, setIsCountrySelectedManually] =
    useState<boolean>(!!props.phoneNumber?.countryIsoCode)
  const selectedCountry = phoneNumberCountries[selectedCountryIndex]
  const selectedCountryCode = selectedCountry.isoCode
  const formattedNumber = getFormattedNumber(selectedCountryCode, phoneNumber)
  const validatedNumber = getValidatedNumber(selectedCountryCode, phoneNumber)

  const onSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault()

    if (shouldAskFirstAndLastName && (!firstName || !lastName)) {
      // this makes the warning show up
      !firstName && setFirstName('')
      !lastName && setLastName('')
      return
    }

    if (!validatedNumber) {
      return
    }

    const phoneNumberInput = createPhoneNumberInput(
      phoneNumber,
      selectedCountry,
    )

    requestVerificationCode(phoneNumberInput, firstName, lastName, hasSMSOptIn)
  }

  useEffect(() => {
    const updateCountryCode = async () => {
      for await (const countryCode of getMachineCountryCode()) {
        const index = phoneNumberCountries.findIndex(country => {
          return country.isoCode === countryCode
        })
        if (index !== -1 && !isCountrySelectedManually) {
          setSelectedCountryIndex(index)
        }
      }
    }
    if (!isCountrySelectedManually) {
      updateCountryCode()
    }
  }, [phoneNumberCountries, isCountrySelectedManually])

  return (
    <form onSubmit={onSubmit} autoComplete="on">
      <Flex flexDirection="column">
        <Text type="subtitle" weight="extraBold" alignment="center">
          {title}
        </Text>
        {subtitle && (
          <Text type="caption" weight="extraBold" alignment="center">
            {subtitle}
          </Text>
        )}
        <Spacer space="x4" />
        {shouldAskFirstAndLastName && (
          <Div
            justifyContent="space-between"
            display="flex"
            flexDirection={'row'}
            outset={{ vertical: 'x1_5' }}
            width="100%"
          >
            <Div width={'49%'}>
              <Input
                id={'first_name_field'}
                onChange={e => setFirstName(e)}
                value={firstName ?? ''}
                placeholder={'First Name'}
                type="text"
                message={
                  firstName !== ''
                    ? undefined
                    : {
                        type: 'danger',
                        content: 'this field is required',
                      }
                }
                isFullWidth={true}
              />
            </Div>
            <Div width={'49%'}>
              <Input
                id={'last_name_field'}
                onChange={e => {
                  setLastName(e)
                }}
                value={lastName ?? ''}
                name="lastName"
                message={
                  lastName !== ''
                    ? undefined
                    : {
                        type: 'danger',
                        content: 'this field is required',
                      }
                }
                placeholder={'Last Name'}
                type="text"
                isFullWidth={true}
              />
            </Div>
          </Div>
        )}
        <Div width={'100%'}>
          <PhoneInput
            countries={phoneNumberCountries}
            selectedCountryIndex={selectedCountryIndex}
            onSelectedCountryIndexChange={index => {
              setIsCountrySelectedManually(true)
              setSelectedCountryIndex(index)
            }}
            phoneNumber={formattedNumber}
            onPhoneNumberChange={setPhoneNumber}
            maxHeight="125px"
            message={
              failureMessage
                ? {
                    type: 'danger',
                    content: failureMessage,
                  }
                : undefined
            }
          />
        </Div>
        <Spacer space="x2" />
        <Div outset={failureMessage ? { top: 'x6' } : undefined}>
          <IconLink
            icon={'information'}
            title={'Why we ask for your phone number...'}
            fill="primaryBodyText"
            onClick={() => setDialogIsOpen(true)}
            size="small"
          />
        </Div>
        <Spacer space={shouldDisplaySmsOptIn ? 'x2' : 'x0'} />
        {shouldDisplaySmsOptIn && (
          <Flex justifyContent="center">
            <FormControlLabel
              control={
                <Checkbox
                  id={'select_all_btn'}
                  checked={hasSMSOptIn}
                  onChange={() => {
                    setHasSMSOptIn(!hasSMSOptIn)
                  }}
                  size="small"
                />
              }
              label={
                <Text
                  type={'footnote'}
                  content={'Send me updates via text message'}
                />
              }
            />
          </Flex>
        )}
        <Spacer space="x2" />
        <Flex flexDirection="column" alignItems="center">
          <Div width={'230px'} id="enter_phone_number_btn">
            <button
              style={{
                borderRadius: '16px',
                display: 'flex',
                justifyContent: 'center',
                boxShadow: '0px 4px 32px 0px rgba(0, 0, 0, 0.08)',
                padding: '8px 20px',
                fontFamily: FONT_FAMILY,
                alignItems: 'center',
                fontWeight: 600,
                color: '#fff',
                fontSize: '16px',
                backgroundColor: '#404040',
                cursor: 'pointer',
                width: '100%',
                height: '48px',
              }}
            >
              {validateButtonTitle ?? 'Validate'}
            </button>
          </Div>
          <Spacer space="x2" />
          {anchorButton && (
            <Anchor
              size={'small'}
              isDecorated={true}
              onClick={() => {
                anchorButton.action()
              }}
            >
              {anchorButton.label}
            </Anchor>
          )}
        </Flex>
        <PhoneVerificationTerms
          shouldDisplaySMSOptInMessage={shouldDisplaySmsOptIn}
        />
        {isDialogOpen && (
          <WhyMyPhoneNumberDialog
            isCreateOrLogin={isCreateOrLogin}
            onAccept={() => setDialogIsOpen(false)}
            onClose={() => setDialogIsOpen(false)}
          />
        )}
      </Flex>
    </form>
  )
}

export default PhoneNumberEntry
